import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import styles from './App.module.css';
import Navbar from './components/Navbar/Navbar';
import PixelOverlay from './components/PixelOverlay/PixelOverlay';
import ChatPane from './components/ChatPane/ChatPane';
import ExplorePage from './pages/ExplorePage/ExplorePage';
import ProfilePage from './pages/ProfilePage/ProfilePage';
import CompleteProfileModal from './components/CompleteProfileModal/CompleteProfileModal';
import useAuth from './hooks/useAuth';
import useProfile from './hooks/useProfile';

// Lazy-loaded pages (not needed on initial homepage render)
const ChannelPage = lazy(() => import('./pages/ChannelPage/ChannelPage'));
const BlockDetailPage = lazy(() => import('./pages/BlockDetailPage/BlockDetailPage'));
const LegacyChannelRedirectPage = lazy(() => import('./pages/LegacyChannelRedirectPage/LegacyChannelRedirectPage'));
const LoginPage = lazy(() => import('./pages/LoginPage/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage/SignupPage'));
const AuthConfirmPage = lazy(() => import('./pages/AuthConfirmPage/AuthConfirmPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage/ResetPasswordPage'));

function NotFound() {
  return (
    <main style={{ padding: 'var(--content-padding)', textAlign: 'center', marginTop: '80px' }}>
      <h1 style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>Page not found</h1>
      <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>The page you're looking for doesn't exist.</p>
    </main>
  );
}

function HomePage() {
  const { session } = useAuth();
  const { profile, loading } = useProfile();

  if (!session) return <ExplorePage />;
  if (loading) return <main />;
  return <ProfilePage handle={profile.handle} />;
}

export default function App() {
  const { session } = useAuth();
  const { profile, loading: profileLoading, refetch } = useProfile();

  // Global profile gate: authenticated users without a profile see CompleteProfileModal
  const needsProfile = session && !profileLoading && (!profile || !profile.handle);

  return (
    <div className={styles.app}>
      {(import.meta.env.MODE === 'staging' || window.location.hostname === 'staging.ego.supply') && <div style={{position:'fixed',top:0,left:'50%',transform:'translateX(-50%)',background:'#facc15',color:'#000',padding:'2px 12px',fontSize:10,fontWeight:700,letterSpacing:1,zIndex:9999,borderRadius:'0 0 4px 4px',pointerEvents:'none'}}>STAGING</div>}
      <Navbar />
      {needsProfile ? (
        <CompleteProfileModal onComplete={refetch} />
      ) : (
      <Suspense fallback={<main />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/explore" element={<ExplorePage />} />
          <Route path="/channels/:slug" element={<LegacyChannelRedirectPage />} />
          <Route path="/:handle/:channelSlug" element={<ChannelPage />}>
            <Route path="blocks/:blockId" element={<BlockDetailPage />} />
          </Route>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/confirm" element={<AuthConfirmPage />} />
          <Route path="/:handle" element={<ProfilePage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      )}
      <ChatPane />
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: 'var(--bg-elevated)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-subtle)',
            fontSize: '13px',
          },
          success: {
            iconTheme: { primary: 'var(--ego-accent)', secondary: 'var(--bg-elevated)' },
          },
          error: {
            iconTheme: { primary: 'var(--error)', secondary: 'var(--bg-elevated)' },
          },
        }}
      />
      <PixelOverlay />
    </div>
  );
}
